import { useCallback, useEffect } from 'react';
import { useFormVehicleId } from './useFormVehicleId';
import { SocketMessage } from 'services/api/types';
import {
  WS_NEW_LISTER_OFFER,
  WS_VIDEO_UPLOAD_COMPLETED,
  WS_VIDEO_UPLOAD_FAILED,
  WS_VIDEO_UPLOAD_STARTED,
  WS_VIDEO_UPLOAD_IN_PROGRESS
} from 'constants/actionTypes';
import toast from 'react-hot-toast';
import { useForm } from 'react-final-form';
import { findSectionPath } from '../utils';
import getSocket from 'services/socket';
import { useInfoToast } from 'components/InfoToast/useInfoToast';
import { db } from 'db';
import { useDispatch } from 'react-redux';
import { setVideoUploadStatus, UploadStatus, useVideoUploadStatus } from 'services/listerUploadVehicle/v2';

const typeToStatus = {
  [WS_VIDEO_UPLOAD_FAILED]: UploadStatus.FAILED,
  [WS_VIDEO_UPLOAD_STARTED]: UploadStatus.IN_PROGRESS,
  [WS_VIDEO_UPLOAD_COMPLETED]: UploadStatus.COMPLETED
};

export const useVideoUploadSocket = () => {
  const vehicleId = useFormVehicleId();

  const status = useVideoUploadStatus(vehicleId);

  const form = useForm();

  const infoToast = useInfoToast();

  const dispatch = useDispatch();

  const listener = useCallback(
    async ({ payload, type }: SocketMessage) => {
      console.log(payload.id, vehicleId, 'payload trigger');
      if (vehicleId !== payload.id) return;
      if (payload.initialMessage) {
        const status = typeToStatus[payload.type];
        if (!status) return;
        return dispatch(setVideoUploadStatus({ vehicleId, status }));
      }
      if (type === WS_VIDEO_UPLOAD_STARTED) {
        dispatch(setVideoUploadStatus({ vehicleId, status: UploadStatus.QUEUED }));
        infoToast('Uploading video to cloud...');
      }
      if (type === WS_VIDEO_UPLOAD_IN_PROGRESS) {
        console.info(payload);
        toast.error('Video upload is already in progress');
      }
      if (type === WS_VIDEO_UPLOAD_FAILED) {
        console.info(payload);
        dispatch(setVideoUploadStatus({ vehicleId, status: UploadStatus.FAILED }));
        toast.error('Video upload failed');
      }
      if (type === WS_VIDEO_UPLOAD_COMPLETED) {
        console.info(payload);
        toast.success('Video upload completed');
        dispatch(setVideoUploadStatus({ vehicleId, status: UploadStatus.COMPLETED }));

        if (payload.url && payload.sectionId) {
          const values = form.getState().values;
          const path = findSectionPath(values, payload.sectionId);
          form.change(`${path}.video`, payload.url);
        }
      }
    },
    [vehicleId]
  );

  const updateLocalOnStatusChange = useCallback(
    async (newStatus: UploadStatus) => {
      if (newStatus === UploadStatus.COMPLETED) {
        await db.videoUploads
          .where('formId')
          .equals(vehicleId)
          .delete();
      } else {
        await db.videoUploads
          .where('formId')
          .equals(vehicleId)
          .modify({ status });
      }
    },
    [vehicleId, db]
  );

  useEffect(() => {
    if (!vehicleId) return;
    const socket = getSocket();
    socket.on('message', listener);
    socket.emit('subscribe', `vehicles-upload-status:${vehicleId}`);

    return () => {
      socket.off('message', listener);
      socket.emit('unsubscribe', `vehicles-upload-status:${vehicleId}`);
    };
  }, [listener, vehicleId]);

  useEffect(() => {
    updateLocalOnStatusChange(status);
  }, [status, updateLocalOnStatusChange]);
};
